import { openChatCommand } from '../../ai-chat/lib/chatCommandHandoff.js';
import { commandRequest } from '../../../agents/context/ui-command-registry.js';
import { useI18nSubscription } from '../../i18n/useI18nSubscription.js';
import { i18n } from '../../../i18n/index.js';
// Блок заметок внутри карточки сущности: создание с автопривязкой + список.
// Единый источник — модель Note (старые «заметки-активности» перенесены миграцией).
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Input, Select, Spin, Tooltip, Typography } from 'antd';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { CheckSquareOutlined } from '@ant-design/icons';

import { formatDateTime } from '../../tasks/lib/taskMeta.js';
import type { NoteItem, NoteLinkInput } from '../api/notes.client.js';
import { DeleteEntityButton } from '../../../shared/ui/DeleteEntityButton.js';
import { NoteFormModal } from './NoteFormModal.js';
import { NoteShareModal } from './NoteShareModal.js';
import { NoteVisibilityTag } from './NoteVisibilityTag.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { useCurrentUser } from '../../../shared/scope/useCurrentScope.js';
import {
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useNotes,
  useUpdateNoteMutation,
} from '../hooks/useNotes.js';

const { Text } = Typography;

interface EntityNotesPanelProps {
  dealId?: string;
  contactId?: string;
  companyId?: string;
  taskId?: string;
  /** `NoteLink` умеет ссылаться и на документ — панель работает и на его карточке. */
  documentId?: string;
  /** И на встречу: заметки по итогам встречи живут на её карточке. */
  calendarEventId?: string;
  title?: string;
}

export function EntityNotesPanel({
  dealId,
  contactId,
  companyId,
  taskId,
  documentId,
  calendarEventId,
  title = i18n.t('crm.features.features.notes.components.entitynotespanel.заметки'),
}: EntityNotesPanelProps): ReactElement {
  useI18nSubscription();
  const filter = {
    ...(dealId ? { dealId } : {}),
    ...(contactId ? { contactId } : {}),
    ...(companyId ? { companyId } : {}),
    ...(taskId ? { taskId } : {}),
    ...(documentId ? { documentId } : {}),
    ...(calendarEventId ? { calendarEventId } : {}),
  };
  // Новая заметка сразу привязывается к открытой сущности.
  const links = Object.keys(filter).length > 0 ? [filter] : [];
  const { data, isLoading, isError, error } = useNotes(filter);
  const createMut = useCreateNoteMutation();
  const updateMut = useUpdateNoteMutation();
  const deleteMut = useDeleteNoteMutation();

  const [draft, setDraft] = useState('');
  // Прикрепление существующей заметки. Отдельной формы с перечнем сущностей нет
  // и не нужно: вторая сторона связи уже известна — это открытая карточка.
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachQuery, setAttachQuery] = useState('');
  // Правка — общая форма заметки, та же, что в разделе «Заметки».
  const [editingNote, setEditingNote] = useState<NoteItem | null>(null);
  const [sharingNote, setSharingNote] = useState<NoteItem | null>(null);
  const currentUser = useCurrentUser();
  const navigate = useNavigate();

  const notes = data ?? [];
  const mutationError = (createMut.error ?? updateMut.error ?? deleteMut.error) as Error | null;

  /** Автор заметки — владелец доступа: кнопка «Поделиться» только у него. */
  const isOwnNote = (note: NoteItem): boolean => note.createdByMember?.userId === currentUser?.id;

  const handleExtractTasks = (note: NoteItem): void => {
    openChatCommand(navigate, commandRequest('extractTasksFromNote', { noteId: note.id }));
  };

  // Заметки всего пространства — из них выбираем, что прикрепить. Запрос идёт
  // только когда выбор открыт: на карточке он не нужен.
  const allNotesQuery = useNotes(attachQuery.trim() ? { q: attachQuery.trim() } : {}, {
    enabled: attachOpen,
  });
  const linkedIds = new Set(notes.map((note) => note.id));
  const attachable = (allNotesQuery.data ?? []).filter((note) => !linkedIds.has(note.id));

  const attachNote = (noteId: string): void => {
    const note = (allNotesQuery.data ?? []).find((candidate) => candidate.id === noteId);
    if (!note) return;

    // `PATCH /notes/:id` заменяет набор привязок целиком, поэтому отправляем
    // прежние плюс текущую сущность — иначе прикрепление рвало бы остальные.
    const existing: NoteLinkInput[] = note.links.map((link) => ({
      ...(link.dealId ? { dealId: link.dealId } : {}),
      ...(link.companyId ? { companyId: link.companyId } : {}),
      ...(link.contactId ? { contactId: link.contactId } : {}),
      ...(link.taskId ? { taskId: link.taskId } : {}),
      ...(link.calendarEventId ? { calendarEventId: link.calendarEventId } : {}),
      ...(link.documentId ? { documentId: link.documentId } : {}),
      ...(link.templateId ? { templateId: link.templateId } : {}),
      ...(link.projectId ? { projectId: link.projectId } : {}),
    }));

    updateMut.mutate(
      { id: noteId, input: { links: [...existing, filter] } },
      {
        onSuccess: () => {
          setAttachOpen(false);
          setAttachQuery('');
        },
      },
    );
  };

  const submitNew = (): void => {
    const body = draft.trim();
    if (!body) return;
    createMut.mutate({ body, links }, { onSuccess: () => setDraft('') });
  };

  return (
    <div className="px-6 py-5">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.07em] text-[var(--ant-color-text-secondary)]">
        {title}
      </div>

      {mutationError ? (
        <Alert
          type="error"
          showIcon
          closable
          className="mb-3"
          title={i18n.t(
            'crm.features.features.notes.components.entitynotespanel.ошибка_операции_с_заметкой',
          )}
          description={mutationError.message}
        />
      ) : null}

      <div className="mb-5">
        <Input.TextArea
          rows={3}
          maxLength={10_000}
          placeholder={i18n.t(
            'crm.features.features.notes.components.entitynotespanel.новая_заметка',
          )}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              submitNew();
            }
          }}
        />
        <div className="mt-2 flex items-center gap-3">
          <Button
            type="primary"
            size="small"
            loading={createMut.isPending}
            disabled={draft.trim().length === 0}
            onClick={submitNew}
          >
            {i18n.t('crm.features.features.notes.components.entitynotespanel.добавить')}
          </Button>
          <Text type="secondary" className="text-xs">
            {i18n.t(
              'crm.features.features.notes.components.entitynotespanel.ctrl_enter_быстро_сохранить',
            )}
          </Text>
          {links.length > 0 ? (
            <Button type="link" size="small" onClick={() => setAttachOpen((open) => !open)}>
              {attachOpen
                ? i18n.t('crm.features.features.notes.components.entitynotespanel.не_прикреплять')
                : i18n.t(
                    'crm.features.features.notes.components.entitynotespanel.прикрепить_существующую',
                  )}
            </Button>
          ) : null}
        </div>

        {attachOpen ? (
          <div className="mt-2">
            <Select
              showSearch
              autoFocus
              style={{ width: '100%' }}
              placeholder={i18n.t(
                'crm.features.features.notes.components.entitynotespanel.найти_заметку_по_тексту',
              )}
              // Ищем на сервере: список заметок пространства усечён, и отбор в
              // браузере пропускал бы подходящие.
              filterOption={false}
              onSearch={setAttachQuery}
              loading={allNotesQuery.isLoading || updateMut.isPending}
              notFoundContent={
                allNotesQuery.isLoading ? (
                  <Spin size="small" />
                ) : (
                  i18n.t(
                    'crm.features.features.notes.components.entitynotespanel.ничего_не_нашлось',
                  )
                )
              }
              onChange={attachNote}
              value={null}
              options={attachable.map((note) => ({
                value: note.id,
                label: note.body.length > 80 ? `${note.body.slice(0, 80)}…` : note.body,
              }))}
            />
          </div>
        ) : null}
      </div>

      {isError ? (
        <ErrorState
          title={i18n.t(
            'crm.features.features.notes.components.entitynotespanel.не_удалось_загрузить_заметки',
          )}
          error={error}
        />
      ) : isLoading ? (
        <div className="flex justify-center py-6">
          <Spin />
        </div>
      ) : notes.length === 0 ? (
        <EmptyState
          text={i18n.t('crm.features.features.notes.components.entitynotespanel.заметок_пока_нет')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="crm-radius-m border border-[var(--ant-color-border-secondary)] bg-[var(--g-fill)] px-4 py-3"
            >
              <div className="mb-2 flex items-center justify-between gap-2">
                <Text type="secondary" className="text-xs">
                  {note.createdByMember?.user.name ??
                    i18n.t(
                      'crm.features.features.notes.components.entitynotespanel.неизвестный_автор',
                    )}{' '}
                  · {formatDateTime(note.createdAt)}
                  <NoteVisibilityTag visibility={note.visibility ?? 'workspace'} />
                </Text>
                <div className="flex gap-2">
                  <Tooltip
                    title={i18n.t(
                      'crm.features.features.notes.components.entitynotespanel.извлечь_задачи_в_план',
                    )}
                  >
                    <Button
                      size="small"
                      icon={<CheckSquareOutlined />}
                      onClick={() => handleExtractTasks(note)}
                    >
                      {i18n.t(
                        'crm.features.features.notes.components.entitynotespanel.извлечь_задачи',
                      )}
                    </Button>
                  </Tooltip>
                  {isOwnNote(note) ? (
                    <Button size="small" onClick={() => setSharingNote(note)}>
                      {i18n.t('crm.features.features.notes.components.entitynotespanel.поделиться')}
                    </Button>
                  ) : null}
                  <Button size="small" onClick={() => setEditingNote(note)}>
                    {i18n.t('crm.features.features.notes.components.entitynotespanel.изменить')}
                  </Button>
                  <DeleteEntityButton
                    label={i18n.t(
                      'crm.features.features.notes.components.entitynotespanel.удалить_заметку',
                    )}
                    title={i18n.t(
                      'crm.features.features.notes.components.entitynotespanel.удалить_заметку_2',
                    )}
                    description={i18n.t(
                      'crm.features.features.notes.components.entitynotespanel.заметка_пропадет_из_списка_и_со_всех_карто',
                    )}
                    loading={deleteMut.isPending && deleteMut.variables === note.id}
                    onConfirm={() => deleteMut.mutate(note.id)}
                  />
                </div>
              </div>
              <Text className="whitespace-pre-wrap text-sm text-[var(--ant-color-text)]">
                {note.body}
              </Text>
            </div>
          ))}
        </div>
      )}

      {editingNote !== null ? (
        <NoteFormModal open note={editingNote} onClose={() => setEditingNote(null)} />
      ) : null}
      {sharingNote !== null ? (
        <NoteShareModal open note={sharingNote} onClose={() => setSharingNote(null)} />
      ) : null}
    </div>
  );
}
